import { CalculatorLink } from '@/components/blog/CalculatorLink'
import { BlogLink } from '@/components/blog/BlogLink'

export default function Article() {
  return (
    <>
      <p>
        WIBOR — wskaźnik, od którego zależy rata milionów polskich kredytów hipotecznych —
        odchodzi do historii. Zastąpi go <strong>POLSTR</strong> (Polish Short Term Rate),
        nowy wskaźnik referencyjny publikowany przez GPW Benchmark od 2 czerwca 2025 roku.
        Sprawdź co to oznacza dla Twojej raty, kiedy nastąpi zmiana i co musisz zrobić jako
        kredytobiorca (spoiler: nic).
      </p>

      <h2 className="text-2xl font-bold text-foreground mt-8 mb-4">Co to jest POLSTR?</h2>
      <p>
        POLSTR (Polish Short Term Rate) to nowy wskaźnik referencyjny krótkoterminowej stopy
        procentowej w Polsce. Jest wyznaczany przez GPW Benchmark — administratora
        wskaźników notowanego pod nadzorem Komisji Nadzoru Finansowego. 1 września 2025 roku
        POLSTR uzyskał status wskaźnika referencyjnego zgodnego z unijnym rozporządzeniem
        BMR (Benchmark Regulation), co pozwala go stosować w nowych umowach kredytowych.
      </p>
      <p className="mt-3">
        Kluczowa różnica wobec WIBOR-u: POLSTR opiera się na <strong>rzeczywistych
        transakcjach overnight</strong> (depozyty jednodniowe między bankami i dużymi
        instytucjami finansowymi), a nie na deklaracjach banków o tym, po jakiej stopie
        <em>mogłyby</em> sobie pożyczyć pieniądze. To zwiększa transparentność i utrudnia
        manipulację — był to główny zarzut wobec WIBOR-u podnoszony przez sądy i KE.
      </p>

      <h2 className="text-2xl font-bold text-foreground mt-8 mb-4">POLSTR vs WIBOR — kluczowe różnice</h2>
      <div className="bg-muted/50 rounded-xl p-4 mt-3">
        <p className="font-semibold mb-2">WIBOR (dotychczasowy wskaźnik)</p>
        <ul className="list-disc pl-6 space-y-1 text-sm">
          <li>Bazuje na <strong>deklaracjach</strong> banków („po ile pożyczyłbym")</li>
          <li>Terminowy — WIBOR 3M, 6M, 12M (na 3, 6, 12 miesięcy do przodu)</li>
          <li>Rata aktualizuje się co 3 lub 6 miesięcy (zależnie od umowy)</li>
          <li>Kwotowany przez panel banków</li>
        </ul>
      </div>

      <div className="bg-muted/50 rounded-xl p-4 mt-3">
        <p className="font-semibold mb-2">POLSTR (nowy wskaźnik)</p>
        <ul className="list-disc pl-6 space-y-1 text-sm">
          <li>Bazuje na <strong>rzeczywistych transakcjach</strong> overnight</li>
          <li>Krótkoterminowy O/N + stopy składane (Compound 1M, 3M, 6M)</li>
          <li>Rata z POLSTR Compound aktualizuje się <strong>co miesiąc</strong></li>
          <li>Wyliczany na podstawie transakcji depozytowych z poprzedniego okresu</li>
        </ul>
      </div>

      <p className="mt-3">
        Pierwsza istotna konsekwencja: <strong>częstszy refresh raty</strong>. Z WIBOR-em 6M
        rata "zamraża się" na pół roku — gdy stopy procentowe spadają, czekasz aż 6 miesięcy
        na korzyść. Z POLSTR Compound nowy poziom kosztu finansowania wchodzi do raty
        co miesiąc. Działa to w obie strony — szybciej skorzystasz na obniżkach{' '}
        <BlogLink slug="obnizki-stop-procentowych-2026">stóp procentowych</BlogLink>, ale
        szybciej odczujesz też ewentualne podwyżki.
      </p>

      <h2 className="text-2xl font-bold text-foreground mt-8 mb-4">Spread korygujący — najważniejsze pojęcie</h2>
      <p>
        Sam POLSTR jest stopą jednodniową — naturalnie różni się od terminowego WIBOR-u 3M
        czy 6M. Aby zamiana wskaźnika w istniejących umowach była ekonomicznie neutralna
        (ani bank, ani kredytobiorca nie powinien zyskać lub stracić z samego faktu
        konwersji), do POLSTR-u doliczy się tzw. <strong>spread korygujący</strong>.
      </p>

      <div className="bg-muted/50 rounded-xl p-4 mt-3 text-center font-semibold text-lg">
        Nowe oprocentowanie = POLSTR Compound + spread korygujący + Twoja marża banku
      </div>

      <p className="mt-3">
        Spread korygujący to różnica między WIBOR-em a POLSTR-em w dniu konwersji,
        wyliczona według metodyki Narodowej Grupy Roboczej ds. reformy wskaźników (NGR).
        Po wyliczeniu jest <strong>stały przez cały pozostały okres kredytu</strong> —
        nie zmienia się wraz ze stopami.
      </p>
      <p className="mt-3">
        Przykład: jeśli w dniu konwersji WIBOR 3M wynosi 3,85%, a POLSTR 3M Compound 3,70%,
        spread korygujący wyniesie ok. 0,15 punktu procentowego. Twoja nowa formuła to
        POLSTR Compound (~3,70%) + 0,15 pp + Twoja marża (np. 2,0%) = ~5,85%. Rata
        powinna pozostać praktycznie identyczna.
      </p>

      <h2 className="text-2xl font-bold text-foreground mt-8 mb-4">Harmonogram reformy 2025-2037</h2>
      <ul className="list-disc pl-6 space-y-2 mt-3">
        <li><strong>2 czerwca 2025</strong> — GPW Benchmark zaczyna publikować POLSTR</li>
        <li><strong>1 września 2025</strong> — POLSTR uzyskuje status zgodny z BMR</li>
        <li><strong>2026</strong> — banki stopniowo wprowadzają produkty oparte o POLSTR</li>
        <li><strong>1 stycznia 2027</strong> — POLSTR jedynym dopuszczonym wskaźnikiem w <strong>nowych</strong> umowach kredytowych</li>
        <li><strong>do końca 2027</strong> — wszystkie umowy z WIBOR-em konwertowane na POLSTR + spread korygujący</li>
        <li><strong>1 stycznia 2037</strong> — GPW Benchmark zaprzestaje publikacji WIBOR-u i WIBID-u</li>
      </ul>

      <p className="mt-3">
        Ministerstwo Finansów przygotowuje ustawę regulującą konwersję — projekt nowelizacji
        ma trafić do Sejmu w III kwartale 2026 roku. Ustawa zapewni podstawę prawną dla
        spreadu korygującego, co eliminuje ryzyko sporów sądowych analogicznych do tych
        z kredytami frankowymi.
      </p>

      <h2 className="text-2xl font-bold text-foreground mt-8 mb-4">Czy moja rata wzrośnie?</h2>
      <p>
        <strong>Nie powinna</strong> — taki jest sens spreadu korygującego. Zasada
        ekonomicznej neutralności mówi, że na moment konwersji rata kredytu nie może
        skokowo wzrosnąć ani spaść wyłącznie z powodu zmiany wskaźnika. Co może się
        zmienić później:
      </p>
      <ul className="list-disc pl-6 space-y-2 mt-3">
        <li><strong>Tempo zmian</strong> — POLSTR Compound aktualizuje się co miesiąc, więc Twoja rata będzie reagować szybciej na decyzje RPP</li>
        <li><strong>Mniej spektakularne skoki</strong> — POLSTR jest mniej zmienny niż terminowy WIBOR, co teoretycznie powinno dawać bardziej stabilne raty</li>
        <li><strong>Marża pozostaje bez zmian</strong> — wynegocjowana marża z umowy nie podlega zmianie przy konwersji</li>
      </ul>

      <div className="mt-4">
        <CalculatorLink wiborRate={3.85} bankMargin={2.0} section="interest-rate">
          Oblicz aktualną ratę przy WIBOR 3,85% + marża 2%
        </CalculatorLink>
      </div>

      <h2 className="text-2xl font-bold text-foreground mt-8 mb-4">Nowe kredyty — czysty POLSTR</h2>
      <p>
        Jeśli bierzesz kredyt hipoteczny w 2026 roku, możesz trafić na ofertę opartą już o
        POLSTR Compound zamiast WIBOR-u. Co istotne: w nowych umowach <strong>nie ma spreadu
        korygującego</strong> — ten mechanizm dotyczy wyłącznie konwersji starych umów.
      </p>
      <p className="mt-3">
        Formuła dla nowych kredytów wygląda więc prosto:
      </p>
      <div className="bg-muted/50 rounded-xl p-4 mt-3 text-center font-semibold text-lg">
        Oprocentowanie = POLSTR Compound + marża banku
      </div>
      <p className="mt-3">
        Porównując oferty, sprawdź <strong>który wskaźnik bank stosuje</strong> (POLSTR
        Compound 3M, 6M, czy może jeszcze WIBOR), bo nominalna wartość różni się o
        kilkanaście-kilkadziesiąt punktów bazowych. Patrz na <BlogLink slug="rrso-kredyt-hipoteczny">RRSO</BlogLink> —
        to jedyna wartość, która pozwala uczciwie porównać kredyty z różnymi wskaźnikami.
      </p>

      <h2 className="text-2xl font-bold text-foreground mt-8 mb-4">Co musisz zrobić jako kredytobiorca?</h2>
      <ul className="list-disc pl-6 space-y-2 mt-3">
        <li><strong>Nic.</strong> Konwersja będzie automatyczna — bank nie wymaga podpisywania aneksu</li>
        <li><strong>Sprawdź pismo od banku</strong> — przed konwersją otrzymasz informację z dokładną wartością spreadu korygującego i nowym harmonogramem spłat</li>
        <li><strong>Zachowaj harmonogram „przed" i „po"</strong> — gdyby coś się nie zgadzało, masz porównanie</li>
        <li><strong>Pamiętaj o miesięcznej aktualizacji</strong> — rata po konwersji może drobno zmieniać się co miesiąc (nawet o kilka złotych), to normalne dla POLSTR Compound</li>
      </ul>

      <h2 className="text-2xl font-bold text-foreground mt-8 mb-4">Co jeszcze warto rozważyć?</h2>
      <p>
        Bez względu na zmianę wskaźnika, mechanizmy obniżania całkowitego kosztu kredytu
        pozostają te same:
      </p>
      <ul className="list-disc pl-6 space-y-2 mt-3">
        <li>
          <BlogLink slug="nadplata-kredytu-hipotecznego">Nadpłata</BlogLink> jest najbardziej
          opłacalna, gdy oprocentowanie jest wysokie — niezależnie od tego, czy bazuje na WIBOR-ze, czy POLSTR-ze
        </li>
        <li>
          Wybór między{' '}
          <BlogLink slug="stale-czy-zmienne-oprocentowanie">stałym a zmiennym oprocentowaniem</BlogLink> {' '}
          nadal aktualny — banki oferują 5-7-letnie okresy stałej stopy również na bazie POLSTR
        </li>
        <li>
          Negocjacja <strong>marży</strong> to wciąż jedyny element formuły, na który masz wpływ
        </li>
      </ul>

      <div className="mt-4">
        <CalculatorLink wiborRate={4.0} bankMargin={2.0} monthlyOverpayment={500} section="overpayment">
          Sprawdź jak nadpłata 500 zł obniża koszt kredytu
        </CalculatorLink>
      </div>

      <h2 className="text-2xl font-bold text-foreground mt-8 mb-4">Czy warto czekać z kredytem na POLSTR?</h2>
      <p>
        Krótko — <strong>nie</strong>. Po pierwsze, sama różnica między WIBOR-em a POLSTR-em
        zostanie skompensowana spreadem korygującym, więc kredyt brany dziś nie będzie
        ekonomicznie gorszy od kredytu branego po konwersji. Po drugie, ceny mieszkań i
        warunki rynkowe zmieniają się szybciej niż wskaźniki bazowe.
      </p>
      <p className="mt-3">
        Lepiej skupić się na tym, co rzeczywiście wpływa na koszt kredytu: marża banku,
        wkład własny, okres kredytowania, RRSO oraz wybór między raty równe vs malejące.
      </p>

      <h2 className="text-2xl font-bold text-foreground mt-8 mb-4">Podsumowanie</h2>
      <p>
        POLSTR to nie kolejna „afera" — to porządkowa zmiana wymuszona unijnym rozporządzeniem
        BMR. Bazuje na realnych transakcjach zamiast deklaracji, aktualizuje ratę częściej
        i jest mniej podatny na manipulacje. Dla obecnych kredytobiorców zmiana będzie
        praktycznie niewidoczna — spread korygujący zachowa neutralność ekonomiczną. Dla
        nowych kredytobiorców POLSTR stanie się standardem od początku 2027 roku. Najlepsze
        co możesz zrobić to zrozumieć formułę swojej raty —{' '}
        <BlogLink slug="co-to-jest-wibor">WIBOR</BlogLink> dziś, POLSTR jutro, mechanika
        oprocentowania ta sama.
      </p>

      <div className="mt-4">
        <CalculatorLink section="affordability">
          Sprawdź swoją ratę w kalkulatorze RatyGo
        </CalculatorLink>
      </div>
    </>
  )
}
